<template>
  <div class="home">
    <link v-if="project && project.fontUrl" :href="project.fontUrl" rel="stylesheet" />
    <div v-if="loading" class="frow centered width-100 height-100">
      <Loader />
    </div>
    <section v-else-if="loadError || stepNotFound" class="page-state" role="status">
      <h1>{{ stepNotFound ? 'Step not found' : 'Project unavailable' }}</h1>
      <p>{{ stepNotFound ? 'The requested step is not available.' : loadError }}</p>
      <router-link v-if="stepNotFound" :to="{ name: 'home', query: $route.query }">
        Return to the artwork
      </router-link>
    </section>
    <div
      v-else
      :class="project.customStyle"
    >
      <transition-group :name="project.transition ? 'fade' : ''" tag="div">
        <div
          v-for="(step, index) in steps"
          v-show="currentStep == index"
          :key="step.id"
          :style="{
            'background-color': project.backgroundColor,
          }"
          class="step"
          @click="showInfo = false"
        >
          <QuoteArtwork
            v-if="step.isQuoteArtwork"
            :ref="`quote-artwork-${step.id}`"
            :active="currentStep == index"
            :artwork="step.quoteArtwork"
            :img-styles="imgStyles"
            :paused="paused"
            :project-id="project.id"
            @sequence-complete="quoteSequenceComplete(step.id)"
            @sequence-reset="quoteSequenceReset(step.id)"
            @timeline-change="quoteTimelineChanged(step.id, $event)"
          />
          <video
            v-else-if="step.video && step.video.url"
            :src="`${API}${step.video.url}`"
            :style="imgStyles"
            class="responsive-image video-no-controls"
            autoplay
            muted
            playsinline
            :id="`myVideo-${index}`"
            @ended="videoEnded"
            :ref="`video-${index}`"
          ></video>
          <img
            v-else-if="step.image"
            :alt="altText(step)"
            :src="`${API}${step.image.url}`"
            :style="imgStyles"
            class="responsive-image"
          />
          <span
            v-html="step.text"
            :style="{
              'color': project.textColor,
              'font-family': project.textFont,
              'text-shadow': project.textShadow,
              'font-size': project.stepTextSize,
            }"
            class="text"
          ></span>
        </div>
      </transition-group>
      <Info
        v-show="showInfo"
        :contributors="contributors"
        :quotes="quoteAttributions"
        :artwork-credit="quoteArtworkCredit"
        :info="project.info"
        :project="project"
        @close="showInfo = false"
      />
      <div class="actions">
        <div class="slider">
          <div class="slider-actions">
            <button
              v-if="quoteTimelineActive && currentStep > 0"
              type="button"
              aria-label="Previous step"
              :style="{'color': project.arrowsColor}"
              class="icon icon-button ion-md-skip-backward"
              @click="popSlide()"
            ></button>
            <button
              type="button"
              :aria-label="quoteTimelineActive ? 'Previous quote' : 'Previous step'"
              :disabled="quoteTimelineActive && sliderValue <= 0"
              :style="{'color': project.arrowsColor}"
              class="icon icon-button ion-md-arrow-dropleft"
              @click="previousControl()"
            ></button>
            <vue-slider
              v-if="stepCount > 0"
              ref="timeline-slider"
              :value="sliderValue"
              :style="{ color: project.buttonColor, padding: '8px 20px' }"
              height="3"
              :dotSize="20"
              :min="0"
              :max="sliderMax"
              :processStyle="{
                backgroundColor: `${project.sliderColor1}`
              }"
               :bgStyle="{
                backgroundColor: `${project.sliderColor2}`
              }"
              :sliderStyle="
                {
                  backgroundColor: `${project.buttonColor}`
                }
              "
              :tooltip="false"
              @callback="sliderChanged"
              @drag-start="sliderDragStart"
            />
            <button
              type="button"
              :aria-label="quoteTimelineActive ? 'Next quote' : 'Next step'"
              :disabled="quoteTimelineActive && sliderValue >= sliderMax"
              :style="{'color': project.arrowsColor}"
              class="icon icon-button ion-md-arrow-dropright"
              @click="nextControl()"
            ></button>
            <button
              v-if="quoteTimelineActive && currentStep < stepCount - 1"
              type="button"
              aria-label="Next step"
              :style="{'color': project.arrowsColor}"
              class="icon icon-button ion-md-skip-forward"
              @click="pushSlide()"
            ></button>
          </div>
        </div>
        <div class="controls-row">
          <div class="sound-controls">
            <i
              :class="muted ? 'ion-md-volume-off' : 'ion-md-volume-high'"
              :style="{'color': muted ? project.soundOffColor : project.soundOnColor}"
              class="icon sound-btn"
              @click="toggleMuted()"
            ></i>
            <i
              v-if="project.backgroundAudio"
              :class="{
                'ion-md-musical-notes': true,
                'icon-off': !backgroundAudioPlaying,
              }"
              :style="{
                color: backgroundAudioPlaying ? project.soundOnColor : project.soundOffColor,
              }"
              class="icon sound-btn"
              @click="toggleBackgroundAudio()"
            ></i>
          </div>
          <button
            type="button"
            :aria-label="playPauseLabel"
            :class="playPauseIconClass"
            :style="{'color': project.buttonColor}"
            class="icon icon-button play"
            @click="togglePause()"
          ></button>
          <div class="info-controls">
            <i
              :style="{'color': project.infoButtonColor || project.buttonColor}"
              class="icon ion-md-information-circle-outline info-btn"
              @click="showInfo = !showInfo"
              @mouseenter="showInfo = true"
            ></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vueSlider from 'vue-slider-component';
import Info from '@/components/Info.vue';
import Loader from '@/components/Loader.vue';
import QuoteArtwork from '@/components/QuoteArtwork.vue';

const { buildPlaybackSteps } = require('@/lib/quote-artwork');
const { safeContributorNames } = require('@/lib/quote-contributors');
const { safeQuoteAttributions, safeArtworkCredit } = require('@/lib/quote-attributions');

// Match the comma-separated domain aliases supported by the public quote form.
function normalizeDomain(value) {
  return String(value || '').trim().toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0].replace(/\.$/, '').replace(/^www\./, '');
}

export default {
  name: 'home',
  components: {
    vueSlider,
    Info,
    Loader,
    QuoteArtwork,
  },
  data() {
    return {
      accessibleSliderInput: null,
      audio: null,
      currentStep: 0,
      contributors: [],
      quoteAttributions: [],
      quoteArtworkCredit: null,
      contributorPollTimer: null,
      contributorRequestGeneration: 0,
      contributorsDestroyed: false,
      muted: false,
      backgroundAudioPlaying: false,
      paused: true,
      steps: [],
      showInfo: false,
      project: {},
      images: [],
      projectDomain: '',
      loading: true,
      loadError: '',
      stepNotFound: false,
      backgroundAudio: null,
      currentVideo: null,
      noAudioAdvanceTimer: null,
      presentationAdvanceTimer: null,
      presentationAdvanceStepId: null,
      presentationRefreshTimer: null,
      quoteSequenceCompleteByStep: {},
      quoteTimelineByStep: {},
      NO_AUDIO_TIMEOUT: 3000,
    };
  },
  computed: {
    contributorProjectId() {
      return this.project && this.project.interactiveQuotesEnabled && this.project.id
        ? this.project.id : null;
    },
    API() {
      return this.$API;
    },
    stepCount() {
      return this.steps.length;
    },
    imgStyles() {
      if (this.project.maxWidth) {
        return { maxWidth: this.project.maxWidth };
      }
      return '';
    },
    currentPlaybackStep() {
      return this.steps[this.currentStep] || null;
    },
    quoteTimelineActive() {
      return Boolean(this.currentPlaybackStep && this.currentPlaybackStep.isQuoteArtwork);
    },
    currentQuoteTimeline() {
      if (!this.quoteTimelineActive) return null;
      return this.quoteTimelineByStep[String(this.currentPlaybackStep.id)] || {
        completed: false,
        playing: false,
        position: 0,
        total: 0,
      };
    },
    sliderValue() {
      return this.quoteTimelineActive ? this.currentQuoteTimeline.position : this.currentStep;
    },
    sliderMax() {
      return this.quoteTimelineActive
        ? this.currentQuoteTimeline.total
        : Math.max(0, this.stepCount - 1);
    },
    sliderAriaValueText() {
      if (this.quoteTimelineActive) {
        return `${this.sliderValue} of ${this.sliderMax} quotes`;
      }
      return `${this.stepCount ? this.currentStep + 1 : 0} of ${this.stepCount} steps`;
    },
    playPauseLabel() {
      if (this.quoteTimelineActive) {
        if (this.currentQuoteTimeline.total === 0 && !this.currentQuoteTimeline.playing) {
          return 'Play quote sequence';
        }
        if (this.currentQuoteTimeline.completed) return 'Restart quote sequence';
        return this.paused ? 'Play quote sequence' : 'Pause quote sequence';
      }
      return this.paused ? 'Play presentation' : 'Pause presentation';
    },
    playPauseIconClass() {
      if (this.quoteTimelineActive) {
        return this.currentQuoteTimeline.playing ? 'ion-md-pause' : 'ion-md-play';
      }
      return this.paused ? 'ion-md-play' : 'ion-md-pause';
    },
  },
  watch: {
    '$route.params.stepTitle': 'applyRouteStep',
    contributorProjectId: {
      immediate: true,
      handler() {
        this.clearContributorPoll();
        this.contributorRequestGeneration += 1;
        this.contributors = [];
        this.quoteAttributions = [];
        this.quoteArtworkCredit = null;
        if (!this.contributorProjectId || this.contributorsDestroyed) return;
        this.getQuoteAcknowledgments();
        // Match approved artwork's 30-second refresh, including while Info is closed.
        this.contributorPollTimer = setInterval(this.getQuoteAcknowledgments, 30000);
      },
    },
  },
  methods: {
    audioEnded() {
      if (!this.paused) {
        this.pushSlide();
      }
    },
    videoEnded() {
      this.pushSlide();
    },
    altText(step) {
      if (step.altText) {
        return step.altText;
      }
      return step.text ? step.text : 'DIMONscape Image';
    },
    // backgroundAudioEnded() {
    //   console.log('BACKGROUND AUDIO ENDED', this.backgroundAudio);
    //   if (!this.paused) {
    //     this.backgroundAudio.play();
    //   }
    //   this.$nextTick(() => {
    //     console.log('background audio', this.backgroundAudio);
    //     console.log('background project audio', this.project.backgroundAudio);
    //   });
    // },
    backgroundAudioErroed(e) {
      console.log('AUDIO ERRORED', e);
    },
    pushSlide() {
      if (this.steps.length === 0) return;
      this.clearNoAudioAdvanceTimer();
      this.clearPresentationAdvanceTimer();
      // Pause all videos before navigating
      this.pauseAllVideos();
      this.currentStep += 1;
      const step = this.steps[this.currentStep];
      if (step) {
        if (step.isQuoteArtwork) {
          if (this.audio) this.audio.pause();
        } else if (step.video && step.video.url) {
          // Video will autoplay and handle its own ended event
          this.$nextTick(() => {
            this.updateCurrentVideo();
          });
        } else if (step.audio && step.audio.url) {
          this.playSound(`${this.API}${step.audio.url}`);
        } else if (!this.isPresentationMode()) {
          this.scheduleNoAudioAdvance();
        }
      } else if (this.project && (this.project.loop || this.isPresentationMode())) {
        this.currentStep = -1;
        this.pushSlide();
      } else {
        // Don't go beyond last step
        this.currentStep = this.steps.length - 1;
      }
      this.setLink();
      this.schedulePresentationAdvance();
    },
    popSlide() {
      if (this.steps.length === 0) return;
      this.clearNoAudioAdvanceTimer();
      this.clearPresentationAdvanceTimer();
      // Pause all videos before navigating
      this.pauseAllVideos();
      this.currentStep = Math.max(0, this.currentStep - 1);
      const step = this.steps[this.currentStep];
      if (step && step.isQuoteArtwork) {
        if (this.audio) this.audio.pause();
      } else if (step && step.video && step.video.url) {
        // Video will autoplay and handle its own ended event
        this.$nextTick(() => {
          this.updateCurrentVideo();
        });
      } else if (step && step.audio && step.audio.url) {
        this.playSound(`${this.API}${step.audio.url}`);
      } else if (!this.isPresentationMode()) {
        this.scheduleNoAudioAdvance();
      }
      this.setLink();
      this.schedulePresentationAdvance();
    },
    previousControl() {
      if (this.quoteTimelineActive) {
        if (this.currentQuoteTimeline.position > 0) {
          this.sliderChanged(this.currentQuoteTimeline.position - 1);
        }
        return;
      }
      this.popSlide();
    },
    nextControl() {
      if (this.quoteTimelineActive) {
        if (this.currentQuoteTimeline.position < this.currentQuoteTimeline.total) {
          this.sliderChanged(this.currentQuoteTimeline.position + 1);
        }
        return;
      }
      this.pushSlide();
    },
    playSound(sound) {
      if (sound) {
        if (!this.audio) {
          this.audio = new Audio();
        }
        if (this.audio) {
          this.audio.pause();
        }
        this.audio.src = sound;
        this.audio.addEventListener('ended', this.audioEnded, false);
        this.audio.muted = this.muted;
        this.audio.play();
      }
    },
    sliderChanged(e) {
      this.clearNoAudioAdvanceTimer();
      if (this.quoteTimelineActive) {
        this.pauseAudio();
        const artwork = this.getCurrentQuoteArtwork();
        if (artwork) artwork.seekQuotePosition(e);
        return;
      }
      this.currentStep = e;
      this.clearPresentationAdvanceTimer();
      this.paused = true;
      // Pause all videos before navigating
      this.pauseAllVideos();
      if (this.steps[e] && this.steps[e].isQuoteArtwork) {
        if (this.audio) this.audio.pause();
      } else if (this.steps[e] && this.steps[e].video && this.steps[e].video.url) {
        // Video will autoplay and handle its own ended event
        this.$nextTick(() => {
          this.updateCurrentVideo();
        });
      } else if (this.steps[e] && this.steps[e].audio && this.steps[e].audio.url) {
        this.playSound(`${this.API}${this.steps[e].audio.url}`);
      } else if (this.audio) {
        this.audio.pause();
      }
      this.setLink();
    },
    sliderDragStart() {
      if (!this.quoteTimelineActive) return;
      this.pauseAudio();
      const artwork = this.getCurrentQuoteArtwork();
      if (artwork) artwork.pausePlayback();
    },
    sliderInputKeyDown(e) {
      const pageStep = Math.max(1, Math.round(this.sliderMax / 10));
      const positions = {
        33: this.sliderValue + pageStep,
        34: this.sliderValue - pageStep,
        35: this.sliderMax,
        36: 0,
        37: this.sliderValue - 1,
        38: this.sliderValue + 1,
        39: this.sliderValue + 1,
        40: this.sliderValue - 1,
      };
      if (!Object.prototype.hasOwnProperty.call(positions, e.keyCode)) return;
      e.preventDefault();
      e.stopPropagation();
      const position = Math.max(0, Math.min(this.sliderMax, positions[e.keyCode]));
      if (position !== this.sliderValue) this.sliderChanged(position);
    },
    sliderInputChanged(e) {
      const value = Number(e.target.value);
      if (!Number.isFinite(value) || value === this.sliderValue) return;
      this.sliderChanged(value);
    },
    updateSliderAccessibility() {
      const slider = this.$refs['timeline-slider'];
      const component = Array.isArray(slider) ? slider[0] : slider;
      const input = component && component.$el
        ? component.$el.querySelector('input[type="range"]')
        : null;
      if (!input) return;
      if (this.accessibleSliderInput && this.accessibleSliderInput !== input) {
        this.accessibleSliderInput.onkeydown = null;
        this.accessibleSliderInput.oninput = null;
        this.accessibleSliderInput.onchange = null;
      }
      this.accessibleSliderInput = input;
      input.onkeydown = this.sliderInputKeyDown;
      input.oninput = this.sliderInputChanged;
      input.onchange = this.sliderInputChanged;
      input.setAttribute(
        'aria-label',
        this.quoteTimelineActive ? 'Quote sequence progress' : 'Presentation step',
      );
      input.setAttribute('aria-valuemin', '0');
      input.setAttribute('aria-valuemax', String(this.sliderMax));
      input.setAttribute('aria-valuenow', String(this.sliderValue));
      input.setAttribute('aria-valuetext', this.sliderAriaValueText);
    },
    toggleMuted() {
      this.muted = !this.muted;
      if (this.audio) {
        this.audio.muted = this.muted;
      }
      if (this.backgroundAudio) {
        this.backgroundAudio.muted = this.muted;
      }
      const video = this.getCurrentVideo();
      if (video) {
        video.muted = this.muted;
      }
    },
    playBackgroundAudio() {
      if (!this.backgroundAudio) {
        return;
      }
      this.backgroundAudio.muted = this.muted;
      const playPromise = this.backgroundAudio.play();
      if (playPromise && playPromise.then) {
        playPromise.then(() => {
          this.backgroundAudioPlaying = true;
        }).catch((error) => {
          this.backgroundAudioPlaying = false;
          console.log('Background audio play blocked:', error);
        });
      } else {
        this.backgroundAudioPlaying = true;
      }
    },
    pauseBackgroundAudio() {
      if (!this.backgroundAudio) {
        return;
      }
      this.backgroundAudio.pause();
      this.backgroundAudioPlaying = false;
    },
    toggleBackgroundAudio() {
      if (!this.backgroundAudio) {
        return;
      }
      if (this.backgroundAudioPlaying && !this.backgroundAudio.paused) {
        this.pauseBackgroundAudio();
      } else {
        this.playBackgroundAudio();
      }
    },
    togglePause() {
      const currentStep = this.steps[this.currentStep];
      if (currentStep && currentStep.isQuoteArtwork) {
        const artwork = this.getCurrentQuoteArtwork();
        if (!artwork) return;
        if (this.currentQuoteTimeline.playing) {
          artwork.pausePlayback();
          if (this.audio) this.audio.pause();
          if (this.backgroundAudio) this.pauseBackgroundAudio();
          this.paused = true;
          this.clearPresentationAdvanceTimer();
        } else {
          artwork.playPlayback();
          if (this.currentQuoteTimeline.playing && this.backgroundAudio) this.playBackgroundAudio();
        }
        this.schedulePresentationAdvance();
        return;
      }
      if (this.paused) {
        const video = this.getCurrentVideo();
        if (video) {
          video.play();
        } else if (this.audio) {
          this.audio.play();
        } else {
          this.scheduleNoAudioAdvance();
        }
        if (this.backgroundAudio) {
          this.playBackgroundAudio();
        }
      } else {
        this.clearNoAudioAdvanceTimer();
        this.clearPresentationAdvanceTimer();
        const video = this.getCurrentVideo();
        if (video) {
          video.pause();
        }
        if (this.audio) {
          this.audio.pause();
        }
        if (this.backgroundAudio) {
          this.pauseBackgroundAudio();
        }
      }
      this.paused = !this.paused;
      this.schedulePresentationAdvance();
    },
    pauseAudio() {
      this.paused = true;
      this.clearNoAudioAdvanceTimer();
      this.clearPresentationAdvanceTimer();
      const video = this.getCurrentVideo();
      if (video) {
        video.pause();
      }
      if (this.audio) {
        this.audio.pause();
      }
      if (this.backgroundAudio) {
        this.pauseBackgroundAudio();
      }
    },
    quoteSequenceComplete(stepId) {
      this.$set(this.quoteSequenceCompleteByStep, String(stepId), true);
      const currentStep = this.steps[this.currentStep];
      if (currentStep && currentStep.id === stepId) {
        this.schedulePresentationAdvance();
      }
    },
    quoteSequenceReset(stepId) {
      this.$set(this.quoteSequenceCompleteByStep, String(stepId), false);
      const currentStep = this.steps[this.currentStep];
      if (currentStep && currentStep.id === stepId) {
        this.clearPresentationAdvanceTimer();
      }
    },
    quoteTimelineChanged(stepId, timeline) {
      const priorTimeline = this.quoteTimelineByStep[String(stepId)];
      this.$set(this.quoteTimelineByStep, String(stepId), timeline);
      const currentStep = this.steps[this.currentStep];
      if (currentStep && String(currentStep.id) === String(stepId)) {
        this.paused = !timeline.playing;
        if (timeline.completed && !(priorTimeline && priorTimeline.completed)) {
          this.pauseBackgroundAudio();
        }
      }
    },
    clearNoAudioAdvanceTimer() {
      if (this.noAudioAdvanceTimer) {
        clearTimeout(this.noAudioAdvanceTimer);
        this.noAudioAdvanceTimer = null;
      }
    },
    scheduleNoAudioAdvance() {
      this.clearNoAudioAdvanceTimer();
      this.noAudioAdvanceTimer = setTimeout(() => {
        this.noAudioAdvanceTimer = null;
        this.audioEnded();
      }, this.NO_AUDIO_TIMEOUT);
    },
    clearPresentationAdvanceTimer() {
      if (this.presentationAdvanceTimer) {
        clearTimeout(this.presentationAdvanceTimer);
        this.presentationAdvanceTimer = null;
      }
      this.presentationAdvanceStepId = null;
    },
    clearPresentationRefreshTimer() {
      if (this.presentationRefreshTimer) {
        clearInterval(this.presentationRefreshTimer);
        this.presentationRefreshTimer = null;
      }
    },
    isPresentationMode() {
      return this.$route.query.mode === 'presentation'
        || this.$route.query.presentation === '1';
    },
    presentationStepDuration() {
      return 3000;
    },
    presentationRefreshInterval() {
      return 30000;
    },
    startPresentationMode() {
      if (!this.isPresentationMode()) {
        return;
      }
      this.paused = false;
      const currentStep = this.steps[this.currentStep];
      if (currentStep && currentStep.isQuoteArtwork) {
        this.schedulePresentationAdvance();
      } else if (currentStep && currentStep.video && currentStep.video.url) {
        this.$nextTick(() => {
          this.updateCurrentVideo();
        });
      } else if (currentStep && currentStep.audio && currentStep.audio.url) {
        this.playSound(`${this.API}${currentStep.audio.url}`);
      } else {
        this.schedulePresentationAdvance();
      }
      this.clearPresentationRefreshTimer();
      this.presentationRefreshTimer = setInterval(() => {
        this.getSteps(false);
      }, this.presentationRefreshInterval());
    },
    schedulePresentationAdvance() {
      const currentStep = this.steps[this.currentStep];
      const completedQuote = currentStep && currentStep.isQuoteArtwork
        && this.quoteSequenceCompleteByStep[String(currentStep.id)] === true;
      if (
        !this.isPresentationMode()
        || (this.paused && !completedQuote)
        || this.steps.length === 0
      ) {
        this.clearPresentationAdvanceTimer();
        return;
      }
      if (currentStep && currentStep.isQuoteArtwork) {
        const sequenceComplete = this.quoteSequenceCompleteByStep[String(currentStep.id)] === true;
        if (!sequenceComplete || this.steps.length === 1) {
          this.clearPresentationAdvanceTimer();
          return;
        }
      }
      const hasVideo = currentStep && !currentStep.isQuoteArtwork
        && currentStep.video && currentStep.video.url;
      const hasAudio = currentStep && !currentStep.isQuoteArtwork
        && currentStep.audio && currentStep.audio.url;
      if (!currentStep || hasVideo || hasAudio) {
        this.clearPresentationAdvanceTimer();
        return;
      }
      const stepId = String(currentStep.id);
      if (
        this.presentationAdvanceTimer
        && this.presentationAdvanceStepId === stepId
      ) return;
      this.clearPresentationAdvanceTimer();
      this.presentationAdvanceStepId = stepId;
      this.presentationAdvanceTimer = setTimeout(() => {
        this.presentationAdvanceTimer = null;
        this.presentationAdvanceStepId = null;
        const liveStep = this.steps[this.currentStep];
        const liveCompletedQuote = liveStep && liveStep.isQuoteArtwork
          && this.quoteSequenceCompleteByStep[String(liveStep.id)] === true;
        if (
          !this.isPresentationMode()
          || (this.paused && !liveCompletedQuote)
          || !liveStep
          || String(liveStep.id) !== stepId
        ) return;
        if (
          liveStep.isQuoteArtwork
          && this.quoteSequenceCompleteByStep[String(liveStep.id)] !== true
        ) return;
        if (liveCompletedQuote && this.paused) {
          this.paused = false;
        }
        this.pushSlide();
      }, this.presentationStepDuration());
    },
    getCurrentVideo() {
      // Get the video element for the current step
      const videoRef = this.$refs[`video-${this.currentStep}`];
      if (Array.isArray(videoRef)) {
        return videoRef[0];
      }
      return videoRef;
    },
    getCurrentQuoteArtwork() {
      if (!this.quoteTimelineActive) return null;
      const artworkRef = this.$refs[`quote-artwork-${this.currentPlaybackStep.id}`];
      return Array.isArray(artworkRef) ? artworkRef[0] : artworkRef;
    },
    pauseAllVideos() {
      // Pause and rewind every mounted video before navigation or replay.
      this.steps.forEach((step, index) => {
        if (step && step.video && step.video.url) {
          const videoRef = this.$refs[`video-${index}`];
          if (videoRef) {
            const video = Array.isArray(videoRef) ? videoRef[0] : videoRef;
            if (video) {
              video.pause();
              video.currentTime = 0;
            }
          }
        }
      });
    },
    updateCurrentVideo() {
      // First, pause all other videos
      this.pauseAllVideos();
      // Update currentVideo reference for the current step
      this.currentVideo = this.getCurrentVideo();
      // Restart video when step is reached
      if (this.currentVideo) {
        this.currentVideo.currentTime = 0;
        // Explicitly start the video with JavaScript
        this.currentVideo.play().catch((error) => {
          // Handle autoplay restrictions - video will need user interaction
          console.log('Video autoplay blocked:', error);
        });
      }
    },
    getProject() {
      const domain = normalizeDomain(this.projectDomain);
      this.loadError = '';
      if (!domain) {
        this.loadError = 'No project is available for this address.';
        this.loading = false;
        return;
      }
      this.axios.get(`${this.$API}/projects?domain_contains=${encodeURIComponent(domain)}`).then((response) => {
        const projects = Array.isArray(response.data) ? response.data : [];
        const matches = projects.filter(project => project && String(project.domain || '')
          .split(',').some(alias => normalizeDomain(alias) === domain));
        if (matches.length !== 1 || !matches[0].id) {
          this.loadError = 'No unique project is available for this address.';
          this.loading = false;
          return;
        }
        [this.project] = matches;
        if (this.project && this.project.id) {
          if (this.project.backgroundAudio && this.project.backgroundAudio.url) {
            this.backgroundAudio = new Audio(`${this.API}${this.project.backgroundAudio.url}`);
            this.backgroundAudio.loop = true;
            // this.backgroundAudio.addEventListener('ended', this.backgroundAudioEnded, false);
            this.backgroundAudio.addEventListener('error', this.backgroundAudioErroed);
            this.backgroundAudio.addEventListener('suspend', this.backgroundAudioErroed);
            this.backgroundAudio.addEventListener('abort', this.backgroundAudioErroed);
            this.backgroundAudio.addEventListener('emptied', this.backgroundAudioErroed);
            this.backgroundAudio.addEventListener('stalled', this.backgroundAudioErroed);
            if (this.project.backgroundAudioVolume) {
              this.backgroundAudio.volume = this.project.backgroundAudioVolume;
            }
          }
          this.getSteps();
        } else {
          console.error('Project not loaded or missing id');
          this.loading = false;
        }
      }).catch((error) => {
        console.error('Error fetching project:', error);
        this.loadError = 'We could not load this project. Please try again later.';
        this.loading = false;
      });
    },
    clearContributorPoll() {
      if (this.contributorPollTimer !== null) {
        clearInterval(this.contributorPollTimer);
        this.contributorPollTimer = null;
      }
    },
    getQuoteAcknowledgments() {
      const projectId = this.contributorProjectId;
      const generation = this.contributorRequestGeneration + 1;
      this.contributorRequestGeneration = generation;
      if (!projectId || this.contributorsDestroyed) {
        this.contributors = [];
        this.quoteAttributions = [];
        this.quoteArtworkCredit = null;
        return;
      }
      const isCurrent = () => !this.contributorsDestroyed
        && generation === this.contributorRequestGeneration
        && projectId === this.contributorProjectId;
      this.axios
        .get(`${this.$API}/projects/${encodeURIComponent(projectId)}/quote-acknowledgments`)
        .then((response) => {
          if (!isCurrent()) return;
          const contributors = response.data && response.data.contributors;
          this.contributors = safeContributorNames(contributors);
          this.quoteAttributions = safeQuoteAttributions(response.data && response.data.quotes);
          this.quoteArtworkCredit = safeArtworkCredit(response.data && response.data.artworkCredit);
        })
        .catch(() => {
          if (isCurrent()) {
            this.contributors = [];
            this.quoteAttributions = [];
            this.quoteArtworkCredit = null;
          }
        });
    },
    setLink() {
      const step = this.steps[this.currentStep];
      if (!step || this.stepNotFound || this.loadError) return;
      const title = step.title && this.urlSafeTitle(step.title);
      const uniqueTitle = title && !/^\d+$/.test(title)
        && this.steps.filter(item => item.title
          && this.urlSafeTitle(item.title) === title).length === 1;
      const stepTitle = uniqueTitle ? title : String(this.currentStep);
      if (this.$route.name === 'step' && this.$route.params.stepTitle === stepTitle) return;
      this.$router.replace({
        name: 'step',
        params: { stepTitle },
        query: this.$route.query,
        hash: this.$route.hash,
      }, () => {}, () => {});
    },
    routeStepIndex() {
      const { stepTitle } = this.$route.params;
      if (stepTitle == null) return 0;
      if (/^\d+$/.test(stepTitle)) {
        const index = Number(stepTitle);
        return String(index) === stepTitle && index < this.steps.length ? index : -1;
      }
      const matches = this.steps.filter(step => step.title
        && this.urlSafeTitle(step.title) === stepTitle);
      return matches.length === 1 ? this.steps.indexOf(matches[0]) : -1;
    },
    applyRouteStep() {
      if (!this.steps.length || this.loadError) return;
      const index = this.routeStepIndex();
      if (index === this.currentStep && !this.stepNotFound) return;
      this.pauseAudio();
      this.pauseAllVideos();
      this.currentStep = index;
      this.stepNotFound = index === -1;
      // Discard the prior scene's sound so Play cannot replay it on the new scene.
      this.audio = null;
      const step = this.steps[index];
      if (step && !step.isQuoteArtwork && step.audio && step.audio.url) {
        this.audio = new Audio(`${this.API}${step.audio.url}`);
        this.audio.muted = this.muted;
        this.audio.addEventListener('ended', this.audioEnded, false);
      }
      if (step && step.video && step.video.url) {
        this.$nextTick(this.updateCurrentVideo);
      }
    },
    getSteps(resetCurrentStep = true) {
      if (!this.project || !this.project.id) {
        console.error('Cannot get steps: project or project.id is missing');
        this.loading = false;
        return;
      }
      this.axios
        .get(`${this.$API}/steps?project=${this.project.id}&_sort=order:ASC&_limit=-1`)
        .then((response) => {
          const previousStep = this.steps[this.currentStep];
          const previousStepId = previousStep && previousStep.id;
          this.steps = buildPlaybackSteps(response.data, this.project);
          const imagePromises = this.steps
            .filter(s => s.image != null && s.image.url)
            .map(s => new Promise((resolve) => {
              const temp = new Image();
              temp.onload = resolve;
              temp.onerror = resolve;
              temp.src = `${this.API}${s.image.url}`;
              this.images.push(temp);
            }));

          Promise.all(imagePromises).then(() => {
            this.loading = false;
          });
          // Validate currentStep is within bounds
          if (this.steps.length > 0) {
            if (resetCurrentStep) {
              this.currentStep = this.routeStepIndex();
              this.stepNotFound = this.currentStep === -1;
            } else if (previousStepId) {
              const updatedStepIndex = this.steps.findIndex(step => step.id === previousStepId);
              this.currentStep = updatedStepIndex !== -1 ? updatedStepIndex : 0;
            }
            if (this.stepNotFound) return;
            this.currentStep = Math.max(0, Math.min(this.currentStep, this.steps.length - 1));
            // Polling preserves identity, but reordered/renamed scenes need a new URL.
            // Update before the unchanged-media early return; the route watcher sees
            // the already selected index and leaves its playback state alone.
            if (!resetCurrentStep) this.setLink();
            const currentStep = this.steps[this.currentStep];
            // Polling must not replace playing audio or rewind an unchanged video.
            const sameMedia = !resetCurrentStep && previousStep && currentStep
              && previousStep.id === currentStep.id
              && previousStep.isQuoteArtwork === currentStep.isQuoteArtwork
              && (previousStep.audio && previousStep.audio.url)
                === (currentStep.audio && currentStep.audio.url)
              && (previousStep.video && previousStep.video.url)
                === (currentStep.video && currentStep.video.url);
            if (sameMedia) {
              this.schedulePresentationAdvance();
              return;
            }
            this.clearNoAudioAdvanceTimer();
            if (this.audio) this.audio.pause();
            this.audio = null;
            if (currentStep && currentStep.isQuoteArtwork) {
              // Quote artwork owns its playback state.
            } else if (currentStep && currentStep.video && currentStep.video.url) {
              this.$nextTick(() => {
                this.updateCurrentVideo();
              });
            } else if (currentStep && currentStep.audio && currentStep.audio.url) {
              this.audio = new Audio(`${this.API}${currentStep.audio.url}`);
              this.audio.muted = this.muted;
              this.audio.addEventListener('ended', this.audioEnded, false);
            }
            if (resetCurrentStep) {
              this.startPresentationMode();
            } else {
              this.schedulePresentationAdvance();
            }
          } else {
            // No steps available
            this.currentStep = 0;
            this.stepNotFound = Boolean(this.$route.params.stepTitle);
          }
        })
        .catch((error) => {
          console.error('Error fetching steps:', error);
          if (resetCurrentStep) this.loadError = 'We could not load the artwork. Please try again later.';
          this.loading = false;
        });
    },
    keyDown(e) {
      if (this.loading || this.loadError || this.stepNotFound) return;
      if (e.keyCode === 39) {
        if (!this.quoteTimelineActive) this.pauseAudio();
        this.nextControl();
      } else if (e.keyCode === 37) {
        if (!this.quoteTimelineActive) this.pauseAudio();
        this.previousControl();
      } else if (
        e.keyCode === 32
        && (!e.target || ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'].indexOf(e.target.tagName) === -1)
      ) {
        this.togglePause();
      }
    },
    urlSafeTitle(title) {
      return title.replace(/\s/g, '-');
    },
  },
  created() {
    this.projectDomain = normalizeDomain(window.location.host);
    if (this.projectDomain === 'dimonscapes.com' || this.projectDomain === 'admin.dimonscapes.com') {
      this.$nextTick(() => {
        this.$router.push('/manage');
      });
    } else {
      this.getProject();
    }
  },
  mounted() {
    window.addEventListener('keydown', this.keyDown);
    this.$nextTick(this.updateSliderAccessibility);
  },
  updated() {
    this.updateSliderAccessibility();
  },
  beforeDestroy() {
    this.contributorsDestroyed = true;
    this.contributorRequestGeneration += 1;
    this.clearContributorPoll();
    this.contributors = [];
    this.quoteAttributions = [];
    this.quoteArtworkCredit = null;
    this.clearNoAudioAdvanceTimer();
    this.clearPresentationAdvanceTimer();
    this.clearPresentationRefreshTimer();
    if (this.accessibleSliderInput) {
      this.accessibleSliderInput.onkeydown = null;
      this.accessibleSliderInput.oninput = null;
      this.accessibleSliderInput.onchange = null;
    }
    window.removeEventListener('keydown', this.keyDown);
  },
};
</script>
<style lang="scss" scoped>
.home {
  height: 100%;
  overflow-y: hidden;
}

.page-state {
  background: #fff;
  color: #222;
  padding: 2rem;
}

.step {
  align-items: center;
  background-color: #000;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: auto;
  max-width: 1200px;
  .text {
    /*color: #fff;*/
    position: absolute;
    margin: auto;
    font-size: 18px;
    margin-top: 10px;
  }
  // .responsive-image {
  //   display: inline-block;
  //   margin: auto;
  //   object-fit: scale-down;
  //   /*max-height: 900px;*/
  //   /*max-width: 1200px;*/
  //   height: 100%;
  //   width: 100%;
  // }
  .responsive-image {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: 100%;
    max-height: 100%;
    margin: auto;
    overflow: auto;
  }
  .video-no-controls {
    // Hide controls in all browsers
    &::-webkit-media-controls {
      display: none !important;
    }
    &::-webkit-media-controls-enclosure {
      display: none !important;
    }
    &::-webkit-media-controls-panel {
      display: none !important;
    }
    &::-webkit-media-controls-play-button {
      display: none !important;
    }
    &::-webkit-media-controls-timeline {
      display: none !important;
    }
    &::-webkit-media-controls-current-time-display {
      display: none !important;
    }
    &::-webkit-media-controls-time-remaining-display {
      display: none !important;
    }
    &::-webkit-media-controls-mute-button {
      display: none !important;
    }
    &::-webkit-media-controls-volume-slider {
      display: none !important;
    }
    &::-webkit-media-controls-fullscreen-button {
      display: none !important;
    }
    // Prevent fullscreen on iOS
    object-fit: contain;
    // Prevent iOS from taking over the screen
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
}

.actions {
  align-items: center;
  bottom: 20px;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  position: absolute;
  width: 100%;
  z-index: 2;
  i,
  .icon-button {
    background: transparent;
    border: 0;
    color: #fff;
    cursor: pointer;
    font-size: 26px;
    padding: 0;
  }
  .icon-button:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
  .icon-button:disabled {
    cursor: default;
    opacity: .45;
  }
  .vue-slider-component:focus-within {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
  .slider {
    width: 80%;
    margin: 0 auto 8px auto;
    .slider-actions {
      align-items: center;
      display: flex;
      width: 100%;
      i,
      .icon-button {
        font-size: 36px;
      }
      .vue-slider-component {
        margin-bottom: 4px;
        width: 100% !important;
      }
    }
  }
  .controls-row {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80%;
    position: relative;
    .sound-controls {
      position: absolute;
      left: 0;
      display: flex;
      gap: 12px;
      .sound-btn {
        cursor: pointer;
      }
    }
    .play {
      cursor: pointer;
    }
    .info-controls {
      position: absolute;
      right: 0;
      .info-btn {
        cursor: pointer;
      }
    }
  }
}

.hidden {
  /*display: none*/
}

.notext4x3 {
  .text {
    display: none;
  }
  .responsive-image {
    margin: 10px auto;
    max-width: 95%;
  }
  // @media (max-width: 991px) {
  .responsive-image {
    max-height: calc(100% - 80px);
  }
  // }
}

.widescreen16x9 {
  .step {
    max-width: 1920px;
    max-height: 1080px;
    aspect-ratio: 16/9;
    justify-content: flex-start;
    align-items: flex-start;
  }
  .text {
    position: absolute;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
  }
  .responsive-image {
    margin: auto;
    max-height: calc(100% - 80px);
    object-fit: contain;
  }
}
// Animations
.fade-enter-active {
  transition: opacity .5s;
}
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.ion-md-musical-notes {
  position: relative;
}

.icon-off:after {
  position: absolute;
  content: '|';
  text-align: center;
  font-size: 1.4em;
  top: -12px;
  left: 10px;
  -webkit-transform: rotate(-45deg);
  -moz-transform: rotate(-45deg);
  -o-transform: rotate(-45deg);
  -ms-transform: rotate(-45deg);
}
</style>
